import {
  PropsWithChildren,
  ReactNode,
  createContext,
  useEffect,
  useState,
} from 'react';
import { Pressable, ScrollView, View } from 'react-native';
import { Text } from '@/components/Text';
import Modal from 'react-native-modal/dist/modal';
import { Modal as TherapistModal } from '@/components/ui/therapist/Modal';
import { PrimaryButton } from '../../../Button';
import {
  DialogContextValue,
  DialogOptions,
  DialogProps,
  ShowDialogFn,
} from './types';
import { isPatientApp } from '@/config/constants';
import { Button } from '@/components/ui/therapist/Button';
import { allowNativeDriver } from '@/utils/allowNativeDriver';
import { Icon } from '@/components/Icon';
import { styles } from './styles';

export const DialogContext = createContext<DialogContextValue | null>(null);
export function DialogProvider({ children }: PropsWithChildren) {
  const [dialogs, setDialogs] = useState<DialogOptions[]>([]);

  const removeDialog = () => {
    setDialogs((p) => p.slice(0, -1));
  };

  const removeAllDialogs = () => {
    setDialogs([]);
  };

  const showDialog: ShowDialogFn = (options: DialogOptions) => {
    setDialogs((p) => [...p, { ...options, id: Math.random() }]);
  };

  const currentDialogOptions = dialogs[dialogs.length - 1];

  return (
    <DialogContext.Provider
      value={{ showDialog, removeDialog, removeAllDialogs }}
    >
      {children}
      {!!currentDialogOptions && (
        <Dialog
          key={currentDialogOptions.id}
          isVisible
          options={currentDialogOptions}
          onClose={removeDialog}
        />
      )}
    </DialogContext.Provider>
  );
}

function Dialog({ isVisible, onClose, ...props }: DialogProps) {
  const [options, setOptions] = useState(props.options);
  const {
    danger = false,
    content,
    contentComponent,
    title,
    cancelOnBackdropPress,
    confirmAction,
    cancelAction,
    cancelable = true,
    headingComponent,
    hideCloseButton,
    hideOkButton,
    hideCloseXButton = true,
  } = options ?? {};

  function handlePressBackdrop() {
    if (!cancelOnBackdropPress) {
      return;
    }
    onClose?.();
  }

  const headerStyles = {
    neutral: styles.headerNeutral,
    danger: styles.headerDanger,
  };

  const showButtonRow =
    (!confirmAction && !cancelAction && !hideOkButton) ||
    !!confirmAction ||
    (!!cancelAction && !!cancelable) ||
    (!cancelAction && !hideCloseButton);

  useEffect(() => {
    setOptions(props.options);
  }, [props.options]);

  if (!isPatientApp) {
    return (
      <TherapistModal
        title={title ?? ''}
        isVisible={isVisible}
        onClose={hideCloseButton ? undefined : onClose}
        cancelActionButton={
          cancelAction ? (
            <Button
              look="secondary"
              onPress={() => cancelAction.onPress(undefined, onClose)}
              label={cancelAction.label}
            />
          ) : undefined
        }
        confirmActionButton={
          <Button
            onPress={() =>
              confirmAction?.onPress(undefined, onClose) ?? onClose?.()
            }
            label={confirmAction?.label ?? 'Ok'}
          />
        }
      >
        {content ? (
          <Text style={styles.contentText}>{content}</Text>
        ) : contentComponent ? (
          contentComponent({ setOptions })
        ) : null}
      </TherapistModal>
    );
  }

  return (
    <Modal
      isVisible={isVisible}
      statusBarTranslucent
      onBackdropPress={handlePressBackdrop}
      useNativeDriver={allowNativeDriver()}
      useNativeDriverForBackdrop={allowNativeDriver()}
      hideModalContentWhileAnimating
      avoidKeyboard
    >
      <View style={styles.container}>
        {!!headingComponent && headingComponent}
        {!!title && (
          <View
            style={[
              styles.titleRow,
              danger ? styles.titleRowDanger : styles.titleRowNeutral,
            ]}
          >
            <Text style={headerStyles[danger ? 'danger' : 'neutral']}>
              {title}
            </Text>
            {!hideCloseXButton && (
              <Pressable
                onPress={onClose}
                hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                style={styles.closeXButton}
              >
                <Icon name="x" size={24} color={danger ? 'white' : undefined} />
              </Pressable>
            )}
          </View>
        )}
        <ScrollView
          style={!!contentComponent ? styles.scrollViewBounded : undefined}
        >
          {!!contentComponent && contentComponent({ setOptions })}
          {!contentComponent && !!content && (
            <View style={styles.contentPadding}>
              {typeof content === 'string' ? (
                <Text style={styles.contentText}>{content}</Text>
              ) : (
                content
              )}
            </View>
          )}
        </ScrollView>

        {showButtonRow ? (
          <View style={styles.buttonRow}>
            {!confirmAction && !cancelAction && !hideOkButton && (
              <PrimaryButton
                look={danger ? 'danger' : 'default'}
                label="OK"
                onPress={onClose}
              />
            )}
            {confirmAction ? (
              <PrimaryButton
                testID="confirm-modal"
                look={danger ? 'danger' : 'default'}
                {...confirmAction}
                onPress={(e) => confirmAction.onPress(e, onClose)}
              />
            ) : null}
            {cancelAction && cancelable ? (
              <PrimaryButton
                look={'neutral'}
                label={cancelAction.label}
                onPress={(e) => cancelAction.onPress(e, onClose)}
              />
            ) : null}
            {!cancelAction && cancelable && !hideCloseButton ? (
              <PrimaryButton
                look={'neutral'}
                onPress={onClose}
                label="Abbrechen"
              />
            ) : null}
          </View>
        ) : null}
      </View>
    </Modal>
  );
}

interface ConfirmDialogProps {
  isVisible?: boolean;
  headerText?: string;
  contentText?: string;
  cancelAction?: () => void;
  cancelText?: string;
  confirmAction?: () => void;
  confirmText?: string;
  children?: ReactNode;
  redBackgroundConfirmButton?: boolean;
}

export function ConfirmDialog({
  isVisible,
  headerText,
  contentText,
  cancelAction,
  cancelText,
  confirmAction,
  confirmText,
  children,
  redBackgroundConfirmButton,
}: ConfirmDialogProps) {
  return (
    <Dialog
      isVisible={isVisible || false}
      onClose={cancelAction}
      options={{
        cancelable: typeof cancelAction === 'function',
        title: headerText,
        content: contentText,
        danger: !!redBackgroundConfirmButton,
        contentComponent: !!children ? () => children : undefined,
        cancelAction: cancelAction
          ? {
              label: cancelText ?? 'Abbrechen',
              onPress: cancelAction,
            }
          : undefined,
        confirmAction: confirmAction
          ? {
              label: confirmText ?? 'Ok',
              onPress: confirmAction,
            }
          : undefined,
      }}
    />
  );
}
